import {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router';

import {useSaveCookieConsent} from '@/entities/user/hooks/useSaveCookieConsent';
import {ONE_SECOND_MS} from '@/shared/consts/time';
import {useFacebookPixel} from '@/shared/hooks/useFacebookPixel';
// import {initPixel as initAxonPixel} from '@/shared/lib/axon';
import {initClarity} from '@/shared/lib/clarity';
import {initFirebaseAnalytics} from '@/shared/lib/firebase';
import {CookieConsent} from '@/shared/lib/localStorage/signUp/cookieConsent';
import {enableRollbarReplay} from '@/shared/lib/rollbar';
// import {initPixel as initTikTokPixel} from '@/shared/lib/tiktok';
import {facebookService} from '@/shared/services/analytics/facebook';
import {cookieConsentService} from '@/shared/services/cookieConsent';

import {INITIAL_MODAL_PREFERENCES} from '../consts';
import {useTrackScreenToFirebaseAnalytics} from '../hooks/useTrackScreenToFirebaseAnalytics';
import {getCookiePreferences} from '../lib/getCookiePreferences';
import {subscribeCookiePreferences} from '../lib/subscribeCookiePreferences';
import {isCookieBannerSuppressedPath} from '../lib/suppressedPaths';
import {CookiePreferences} from '../types';

type CookieConsentModalOpenMode = 'consent' | 'preferences';

type PreferencesModalState = {
  isForcedOpen: boolean;
  initialPreferences: CookiePreferences;
};

type UseCookieConsent = {
  isOpen: boolean;
  openMode: CookieConsentModalOpenMode;
  initialPreferences: CookiePreferences;
  onDecision: (consent: CookieConsent['categories']) => void;
  onClosePreferencesMode: () => void;
};

const INITIAL_PREFERENCES_MODAL_STATE: PreferencesModalState = {
  isForcedOpen: false,
  initialPreferences: INITIAL_MODAL_PREFERENCES,
};

export function useCookieConsent(): UseCookieConsent {
  const cypressEnv = typeof window !== 'undefined' && Boolean(window.Cypress);
  const [showModal, setShowModal] = useState(false);
  const [isMarketingAllowed, setIsMarketingAllowed] = useState(false);
  const [preferencesModalState, setPreferencesModalState] =
    useState<PreferencesModalState>(INITIAL_PREFERENCES_MODAL_STATE);
  const {mutateAsync: saveUserCookieConsent} = useSaveCookieConsent();

  const snapshotBeforeOpenRef = useRef<CookieConsent | null>(null);

  const {pathname} = useLocation();
  const isCookieBannerSuppressed = isCookieBannerSuppressedPath(pathname);

  const {initPixel} = useFacebookPixel();
  useTrackScreenToFirebaseAnalytics({
    skipInit: cypressEnv || !isMarketingAllowed,
  });

  useEffect(() => {
    const storedConsent = cookieConsentService.readFromStorage();
    setShowModal(!storedConsent && !isCookieBannerSuppressed);

    let cancelled = false;

    (async () => {
      const consent = await cookieConsentService.waitForDecision();
      if (cancelled) {
        return;
      }

      if (consent.categories.statistics) {
        enableRollbarReplay();

        if (!cypressEnv) {
          initClarity();
        }
      }

      if (consent.categories.marketing && !cypressEnv) {
        setIsMarketingAllowed(true);
        initFirebaseAnalytics();
        initPixel();
        // put off support for Axon and TikTok pixels
        // initAxonPixel();
        // initTikTokPixel();
      } else {
        facebookService.disable();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cypressEnv, initPixel, isCookieBannerSuppressed]);

  const openPreferencesMode = useCallback(
    (storedConsent: CookieConsent | null) => {
      snapshotBeforeOpenRef.current = storedConsent;

      setPreferencesModalState({
        isForcedOpen: true,
        initialPreferences: getCookiePreferences(storedConsent),
      });
    },
    []
  );

  const agreeCookiePreferences = useCallback(
    (storedConsent: CookieConsent | null) => {
      if (storedConsent) {
        return;
      }
      openPreferencesMode(storedConsent);
    },
    [openPreferencesMode]
  );

  const closePreferencesMode = useCallback(() => {
    snapshotBeforeOpenRef.current = null;
    setPreferencesModalState(INITIAL_PREFERENCES_MODAL_STATE);
  }, []);

  useEffect(() => {
    return subscribeCookiePreferences('open-cookie-preferences', () => {
      const storedConsent = cookieConsentService.readFromStorage();
      openPreferencesMode(storedConsent);
    });
  }, [openPreferencesMode]);

  useEffect(() => {
    return subscribeCookiePreferences('agree-cookie-preferences', () => {
      const storedConsent = cookieConsentService.readFromStorage();
      agreeCookiePreferences(storedConsent);
    });
  }, [agreeCookiePreferences]);

  const openMode: CookieConsentModalOpenMode =
    cookieConsentService.readFromStorage() ? 'preferences' : 'consent';

  const handleCookieDecision: UseCookieConsent['onDecision'] = useCallback(
    (categories) => {
      const changed = cookieConsentService.saveCategoriesDecision(categories);
      setShowModal(false);
      closePreferencesMode();

      if (changed) {
        saveUserCookieConsent().catch(() => {
          // noop
        });
      }

      closePreferencesMode();

      if (openMode !== 'preferences') {
        return;
      }

      if (changed && typeof window !== 'undefined') {
        setTimeout(() => {
          window.location.reload();
        }, ONE_SECOND_MS);
      }
    },
    [closePreferencesMode, saveUserCookieConsent, openMode]
  );

  return {
    isOpen:
      (showModal && !isCookieBannerSuppressed) ||
      preferencesModalState.isForcedOpen,
    openMode,
    initialPreferences: preferencesModalState.initialPreferences,
    onDecision: handleCookieDecision,
    onClosePreferencesMode: closePreferencesMode,
  };
}
